import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * RichTextBox (senza librerie)
 * - Toolbar: Bold, Underline, Strike, Align center/left, Color picker
 * - contentEditable editor
 * - Resizable (vertical)
 * - Compatibile con handleChange: onChange({ target: { name, value: html } })
 *
 * Props:
 *  - label, name, value (HTML string), onChange, placeholder
 *  - disabled
 *  - minHeight, maxHeight
 */
export default function RichTextBox({
  label,
  name,
  value,
  onChange,
  placeholder = "Scrivi qui...",
  disabled = false,
  required = false,
  minHeight = 120,
  maxHeight = 360,
}) {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const safeValue = useMemo(() => (value ?? ""), [value]);

  // Mantiene l'HTML in sync quando arriva dall'esterno (es. edit record)
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    // Evita di “resettare” il cursore mentre scrivi:
    if (document.activeElement === el) return;
    el.innerHTML = safeValue || "";
  }, [safeValue]);

  const emitChange = () => {
    const html = editorRef.current?.innerHTML ?? "";
    onChange?.({ target: { name, value: html } });
  };

  const exec = (command, commandValue = null) => {
    if (disabled) return;
    editorRef.current?.focus();
    // execCommand è deprecato ma ancora supportato nella maggior parte dei browser e va benissimo per gestionali interni
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const setColor = (hex) => {
    exec("foreColor", hex);
  };

  const hasContent = () => {
    const el = editorRef.current;
    if (!el) return false;
    const txt = el.innerText?.trim() ?? "";
    return txt.length > 0;
  };

  return (
    <label style={{ display: "grid", gap: 6 }}>
      {label && (
        <span style={{ color: "white", fontSize: 14 }}>
          {label} {required && <span style={{ color: "#f87171" }}>*</span>}
        </span>
      )}

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
          padding: 8,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(255,255,255,0.04)",
        }}
      >
        <ToolButton disabled={disabled} onClick={() => exec("bold")} title="Grassetto">
          B
        </ToolButton>

        <ToolButton disabled={disabled} onClick={() => exec("underline")} title="Sottolineato">
          U
        </ToolButton>

        <ToolButton
          disabled={disabled}
          onClick={() => exec("strikeThrough")}
          title="Barrato"
        >
          S̶
        </ToolButton>

        <Divider />

        <ToolButton disabled={disabled} onClick={() => exec("justifyLeft")} title="Allinea a sinistra">
          ⬅
        </ToolButton>

        <ToolButton disabled={disabled} onClick={() => exec("justifyCenter")} title="Centra">
          ⬌
        </ToolButton>

        <Divider />

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>Colore</span>
          <input
            type="color"
            disabled={disabled}
            onChange={(e) => setColor(e.target.value)}
            style={{
              width: 34,
              height: 28,
              padding: 0,
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 8,
              background: "transparent",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            title="Colore font"
          />
        </div>

        <div style={{ flex: 1 }} />

        <ToolButton
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            editorRef.current?.focus();
            document.execCommand("removeFormat", false, null);
            emitChange();
          }}
          title="Rimuovi formattazione"
        >
          Reset
        </ToolButton>
      </div>

      {/* Editor resizable */}
      <div
        style={{
          borderRadius: 12,
          border: isFocused
            ? "1px solid rgba(59,130,246,0.9)"
            : "1px solid rgba(255,255,255,0.15)",
          background: "linear-gradient(180deg, #1f2937 0%, #111827 100%)",
          boxShadow: isFocused
            ? "0 0 0 1px rgba(59,130,246,0.35)"
            : "inset 0 1px 2px rgba(0,0,0,0.4)",
          overflow: "auto",
          resize: "vertical", // ✅ l’utente può allungare
          minHeight,
          maxHeight,
        }}
      >
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emitChange}
          onBlur={() => setIsFocused(false)}
          onFocus={() => setIsFocused(true)}
          style={{
            padding: "10px 12px",
            minHeight: minHeight - 2,
            color: "white",
            outline: "none",
            fontFamily: "inherit",
            fontSize: 14,
            lineHeight: "22px",
            cursor: disabled ? "not-allowed" : "text",
            opacity: disabled ? 0.65 : 1,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        />

        {/* Placeholder (finto) */}
        {!disabled && !isFocused && !hasContent() && (
          <div
            style={{
              position: "absolute",
              // per farlo semplice senza positioning complesso: lo metto con pointerEvents none e margini simili
              pointerEvents: "none",
              color: "rgba(255,255,255,0.45)",
              marginTop: -minHeight + 12, // “porta su” il placeholder
              padding: "10px 12px",
              fontSize: 14,
            }}
          >
            {placeholder}
          </div>
        )}
      </div>

      {/* Hint */}
      <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 12 }}>
        Suggerimento: puoi allungare il riquadro trascinando l’angolo in basso a destra.
      </div>
    </label>
  );
}

function ToolButton({ children, onClick, disabled, title }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()} // evita perdere selezione nel contentEditable
      onClick={onClick}
      title={title}
      style={{
        padding: "6px 10px",
        borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.15)",
        background: disabled ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.10)",
        color: "white",
        cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: 800,
        fontSize: 12,
      }}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 22, background: "rgba(255,255,255,0.12)" }} />;
}